
import { createTheme } from '@mui/material/styles';

// Enhanced Bluish Aesthetic Theme with expanded color palette
const colors = {
  primary: '#00d4ff', 
  secondary: '#4361ee',
  accent1: '#3a86ff',
  accent2: '#4cc9f0',
  accent3: '#0077b6',
  accent4: '#38bdf8',
  accent5: '#0ea5e9',
  accent6: '#2563eb',
  accent7: '#60a5fa', // New accent color
  accent8: '#93c5fd', // New accent color
  accent9: '#1e40af', // Deep blue accent
  background: '#030b16',
  surface: ['#0a1929', '#142c44'],
  text: {
    primary: '#ffffff',
    secondary: '#b0c5d9',
    accent: '#7dd3fc', // Light blue text accent
  },
  gradients: {
    primary: 'linear-gradient(135deg, #00d4ff 0%, #0077b6 100%)',
    secondary: 'linear-gradient(135deg, #4361ee 0%, #3a86ff 100%)',
    accent: 'linear-gradient(135deg, #4cc9f0 0%, #00d4ff 100%)',
    blue: 'linear-gradient(135deg, #38bdf8 0%, #2563eb 100%)',
    cyan: 'linear-gradient(135deg, #22d3ee 0%, #0891b2 100%)',
    indigo: 'linear-gradient(135deg, #818cf8 0%, #4f46e5 100%)',
    glass: 'linear-gradient(135deg, rgba(10, 25, 41, 0.7) 0%, rgba(20, 44, 68, 0.5) 100%)',
    shimmer: 'linear-gradient(45deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.03) 50%, rgba(255,255,255,0) 100%)',
    cosmic: 'linear-gradient(135deg, #0c4a6e 0%, #0369a1 50%, #0ea5e9 100%)', // New cosmic gradient
    aurora: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 50%, #22d3ee 100%)', // New aurora gradient
  }
};

const typography = {
  fontFamily: 'Inter, Roboto, Helvetica, Arial, sans-serif',
  h1: {
    fontSize: '3.5rem',
    fontWeight: 700,
    background: 'linear-gradient(135deg, #00d4ff 0%, #0077b6 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    letterSpacing: '-0.02em',
  },
  h2: {
    fontSize: '2.75rem',
    fontWeight: 600,
    background: 'linear-gradient(135deg, #00d4ff 0%, #0077b6 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  body1: {
    fontSize: '1rem',
    fontWeight: 400,
    letterSpacing: '0.01em',
  },
};

// Enhanced Animation definitions for parallax and visual effects
const animations = {
  keyframes: `
    @keyframes float {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-20px); }
    }
    @keyframes floatSlow {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-10px); }
    }
    @keyframes floatHorizontal {
      0%, 100% { transform: translateX(0px); }
      50% { transform: translateX(20px); }
    }
    @keyframes floatDiagonal {
      0%, 100% { transform: translate(0px, 0px); }
      50% { transform: translate(15px, -15px); }
    }
    @keyframes floatWave {
      0% { transform: translate(0px, 0px); }
      25% { transform: translate(10px, -15px); }
      50% { transform: translate(20px, 0px); }
      75% { transform: translate(10px, 15px); }
      100% { transform: translate(0px, 0px); }
    }
    @keyframes floatOrbit {
      0% { transform: translate(0px, 0px) rotate(0deg); }
      100% { transform: translate(0px, 0px) rotate(360deg); }
    }
    @keyframes rotate {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    @keyframes rotateReverse {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(-360deg); }
    }
    @keyframes rotateSlow {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    @keyframes rotateX {
      0% { transform: rotateX(0deg); }
      100% { transform: rotateX(360deg); }
    }
    @keyframes rotateY {
      0% { transform: rotateY(0deg); }
      100% { transform: rotateY(360deg); }
    }
    @keyframes pulse {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.8; transform: scale(1.05); }
    }
    @keyframes pulseFast {
      0%, 100% { transform: scale(1); opacity: 1; }
      50% { transform: scale(1.03); opacity: 0.9; }
    }
    @keyframes pulseStrong {
      0%, 100% { transform: scale(1); opacity: 1; }
      50% { transform: scale(1.08); opacity: 0.8; }
    }
    @keyframes shimmer {
      0% { background-position: -1000px 0; }
      100% { background-position: 1000px 0; }
    }
    @keyframes shimmerRadial {
      0% { background-size: 100% 100%; }
      50% { background-size: 120% 120%; }
      100% { background-size: 100% 100%; }
    }
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes slideUp {
      from { transform: translateY(50px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
    @keyframes slideDown {
      from { transform: translateY(-50px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
    @keyframes slideLeft {
      from { transform: translateX(50px); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }
    @keyframes slideRight {
      from { transform: translateX(-50px); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }
    @keyframes glow {
      0%, 100% { box-shadow: 0 0 5px rgba(0, 212, 255, 0.5); }
      50% { box-shadow: 0 0 20px rgba(0, 212, 255, 0.8); }
    }
    @keyframes glowText {
      0%, 100% { text-shadow: 0 0 5px rgba(0, 212, 255, 0.2); }
      50% { text-shadow: 0 0 15px rgba(0, 212, 255, 0.6); }
    }
    @keyframes scaleIn {
      from { transform: scale(0.9); opacity: 0; }
      to { transform: scale(1); opacity: 1; }
    }
    @keyframes borderPulse {
      0%, 100% { border-color: rgba(0, 212, 255, 0.2); }
      50% { border-color: rgba(0, 212, 255, 0.8); }
    }
    @keyframes gradientFlow {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
  `,
  transitions: {
    default: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    fast: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
    slow: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
    bounce: 'all 0.5s cubic-bezier(0.68, -0.55, 0.27, 1.55)',
    spring: 'all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    elastic: 'all 0.8s cubic-bezier(0.5, 1.5, 0.75, 1.25)',
    smooth: 'all 0.7s cubic-bezier(0.33, 1, 0.68, 1)',
    decelerate: 'all 0.6s cubic-bezier(0, 0, 0.2, 1)',
    accelerate: 'all 0.4s cubic-bezier(0.4, 0, 1, 1)',
    anticipate: 'all 0.6s cubic-bezier(0.38, -0.4, 0.88, 0.65)',
  },
  durations: {
    veryFast: '0.15s',
    fast: '0.3s',
    medium: '0.5s',
    slow: '0.8s',
    verySlow: '1.2s',
    extraSlow: '2s',
    ultraSlow: '3s',
  }
};

const newTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: colors.primary,
      light: colors.accent2,
      dark: colors.accent3,
    },
    secondary: {
      main: colors.secondary,
      light: colors.accent1,
    },
    background: {
      default: colors.background,
      paper: colors.surface[0],
      glass: 'rgba(10, 25, 41, 0.7)',
    },
    text: {
      primary: colors.text.primary,
      secondary: colors.text.secondary,
    },
  },
  typography,
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        ${animations.keyframes}
        
        html {
          scroll-behavior: smooth;
        }
        
        body {
          background-color: ${colors.background};
          background-image: radial-gradient(circle at 50% 0%, ${colors.accent3}22, transparent 25%),
                            radial-gradient(circle at 10% 30%, ${colors.accent2}15, transparent 15%),
                            radial-gradient(circle at 80% 80%, ${colors.accent4}10, transparent 20%);
          background-size: 100% 100%;
          background-attachment: fixed;
          overflow-x: hidden;
        }

        @media (max-width: 768px) {
          body {
            background-attachment: scroll; /* Fix mobile scroll jitter */
          }
        }
        
        ::-webkit-scrollbar {
          width: 8px;
        }
        
        ::-webkit-scrollbar-track {
          background: ${colors.surface[0]};
        }
        
        ::-webkit-scrollbar-thumb {
          background: ${colors.accent3};
          border-radius: 4px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: ${colors.primary};
        }
        
        .parallax-bg {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 0;
        }
        
        /* Enhanced animation classes */
        .glow-effect {
          animation: glow 3s infinite;
        }
        
        .text-glow-effect {
          animation: glowText 3s infinite;
        }
        
        .float-animation {
          animation: float 6s ease-in-out infinite;
        }
        
        .float-slow-animation {
          animation: floatSlow 8s ease-in-out infinite;
        }
        
        .float-horizontal-animation {
          animation: floatHorizontal 8s ease-in-out infinite;
        }
        
        .float-diagonal-animation {
          animation: floatDiagonal 8s ease-in-out infinite;
        }
        
        .float-wave-animation {
          animation: floatWave 10s ease-in-out infinite;
        }
        
        .float-orbit-animation {
          animation: floatOrbit 15s linear infinite;
        }
        
        .rotate-animation {
          animation: rotate 20s linear infinite;
        }
        
        .rotate-reverse-animation {
          animation: rotateReverse 20s linear infinite;
        }
        
        .rotate-slow-animation {
          animation: rotate 30s linear infinite;
        }
        
        .rotate-x-animation {
          animation: rotateX 15s linear infinite;
          transform-style: preserve-3d;
        }
        
        .rotate-y-animation {
          animation: rotateY 15s linear infinite;
          transform-style: preserve-3d;
        }
        
        .pulse-animation {
          animation: pulse 4s ease-in-out infinite;
        }
        
        .pulse-fast-animation {
          animation: pulseFast 2s ease-in-out infinite;
        }
        
        .pulse-strong-animation {
          animation: pulseStrong 3s ease-in-out infinite;
        }
        
        .shimmer-animation {
          background: ${colors.gradients.shimmer};
          background-size: 200% 100%;
          animation: shimmer 3s infinite linear;
        }
        
        .shimmer-radial-animation {
          animation: shimmerRadial 4s ease-in-out infinite;
        }
        
        .gradient-flow-animation {
          background-size: 200% 200%;
          animation: gradientFlow 8s ease infinite;
        }
        
        .border-pulse-animation {
          animation: borderPulse 3s infinite;
        }
        
        .cosmic-gradient {
          background: ${colors.gradients.cosmic};
          background-size: 200% 200%;
          animation: gradientFlow 10s ease infinite;
        }
        
        .aurora-gradient {
          background: ${colors.gradients.aurora};
          background-size: 200% 200%;
          animation: gradientFlow 8s ease infinite;
        }
        
        /* Gradient text */
        .gradient-text {
          background: ${colors.gradients.primary};
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          text-fill-color: transparent;
        }
        
        .gradient-text-blue {
          background: ${colors.gradients.blue};
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          text-fill-color: transparent;
        }
        
        .gradient-text-cyan {
          background: ${colors.gradients.cyan};
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          text-fill-color: transparent;
        }
        
        /* Glass morphism */
        .glass-morphism {
          background: rgba(10, 25, 41, 0.7);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.08);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
        }
      `,
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
          textTransform: 'none',
          fontWeight: 600,
          transition: animations.transitions.bounce,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '100%',
            height: '100%',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
            transition: 'all 0.5s',
          },
          '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: `0 10px 25px rgba(0, 212, 255, 0.4)`,
            '&::before': {
              left: '100%',
            },
          },
          '&:active': {
            transform: 'translateY(-2px)',
          },
        },
        contained: {
          background: colors.gradients.primary,
          backgroundSize: '200% 200%',
          animation: 'gradientFlow 8s ease infinite',
          '&:hover': {
            background: colors.gradients.accent,
            backgroundSize: '200% 200%',
          },
        },
        outlined: {
          borderColor: colors.primary,
          borderWidth: '2px',
          '&:hover': {
            borderColor: colors.accent2,
            borderWidth: '2px',
            backgroundColor: 'rgba(0, 212, 255, 0.08)',
          },
        },
        // New variants
        containedSecondary: {
          background: colors.gradients.blue,
          backgroundSize: '200% 200%',
          animation: 'gradientFlow 8s ease infinite',
          '&:hover': {
            background: colors.gradients.indigo,
            backgroundSize: '200% 200%',
          },
        },
        // Size variants
        sizeLarge: {
          padding: '12px 28px',
          fontSize: '1rem',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          backdropFilter: 'blur(10px)',
          backgroundColor: 'rgba(10, 25, 41, 0.7)',
          backgroundImage: 'linear-gradient(135deg, rgba(10, 25, 41, 0.8) 0%, rgba(20, 44, 68, 0.6) 100%)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          transition: animations.transitions.spring,
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, transparent 100%)',
            opacity: 0,
            transition: 'opacity 0.3s ease',
          },
          '&:hover': {
            transform: 'translateY(-8px) scale(1.02)',
            boxShadow: '0 15px 40px rgba(0, 212, 255, 0.2)',
            '&::before': {
              opacity: 1,
            },
          },
          '&.glow-effect': {
            '&:hover': {
              boxShadow: '0 0 25px rgba(0, 212, 255, 0.5), 0 15px 40px rgba(0, 0, 0, 0.2)',
            }
          },
          '&.border-pulse': {
            animation: 'borderPulse 3s infinite',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: colors.gradients.glass,
          backdropFilter: 'blur(10px)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'rgba(3, 11, 22, 0.7)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          transition: animations.transitions.default,
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0, 212, 255, 0.2)',
          },
        },
        filled: {
          background: colors.gradients.primary,
        },
      },
    },
  },
});

// Add custom properties to theme for use in components
newTheme.colors = colors;
newTheme.animations = animations;

export default newTheme;
